import axios from "axios";
import { API_URL } from "./config";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield, ExternalLink, Info, Eye, EyeOff,
  ArrowRight, ArrowLeft, ChevronDown, CheckCircle2,
  PenLine, FileSignature, ShieldCheck, User, FileClock, UserCog, Check,
} from "lucide-react";



/* ─── Countries ─────────────────────────────────────────── */
const COUNTRIES = [
  { code: "in",  flag: "🇮🇳", dial: "+91",  name: "India" },
  { code: "us",  flag: "🇺🇸", dial: "+1",   name: "United States" },
  { code: "gb",  flag: "🇬🇧", dial: "+44",  name: "United Kingdom" },
  { code: "au",  flag: "🇦🇺", dial: "+61",  name: "Australia" },
  { code: "ca",  flag: "🇨🇦", dial: "+1",   name: "Canada" },
  { code: "de",  flag: "🇩🇪", dial: "+49",  name: "Germany" },
  { code: "fr",  flag: "🇫🇷", dial: "+33",  name: "France" },
  { code: "jp",  flag: "🇯🇵", dial: "+81",  name: "Japan" },
  { code: "sg",  flag: "🇸🇬", dial: "+65",  name: "Singapore" },
  { code: "ae",  flag: "🇦🇪", dial: "+971", name: "UAE" },
];

/* ─── Roles ──────────────────────────────────────────────── */
const ROLES = {
  User: {
    icon: User,
    desc: "Can sign documents, request signatures and view allowed documents.",
    perms: [
      { label: "Sign Documents",        icon: PenLine },
      { label: "Request Signatures",    icon: FileSignature },
      { label: "Verify Documents",      icon: ShieldCheck },
      { label: "View Documents",        icon: Eye },
      { label: "Manage Own Profile",    icon: User },
      { label: "View Audit Logs (Own)", icon: FileClock },
    ],
  },
  Manager: {
    icon: UserCog,
    desc: "Can manage team members and oversee document workflows.",
    perms: [
      { label: "Sign Documents",        icon: PenLine },
      { label: "Request Signatures",    icon: FileSignature },
      { label: "Verify Documents",      icon: ShieldCheck },
      { label: "View Documents",        icon: Eye },
      { label: "Manage Team",           icon: UserCog },
      { label: "View Team Audit Logs",  icon: FileClock },
    ],
  },
  Admin: {
    icon: Shield,
    desc: "Full access to manage users, roles, and organization settings.",
    perms: [
      { label: "Sign Documents",        icon: PenLine },
      { label: "Request Signatures",    icon: FileSignature },
      { label: "Verify Documents",      icon: ShieldCheck },
      { label: "Manage All Users",      icon: UserCog },
      { label: "Manage Organization",   icon: Shield },
      { label: "View All Audit Logs",   icon: FileClock },
    ],
  },
};

const STEPS = ["User Information", "Roles & Permissions", "Review & Confirm"];

/* ─── Scoped CSS ─────────────────────────────────────────── */
const CSS = `
  .au-wrap{background:#fff;font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;color:#111827;}

  /* cards */
  .au-card{border:1px solid #E5E7EB;border-radius:12px;padding:20px 22px;background:#fff;}
  .au-card-title{font-size:15px;font-weight:700;color:#111827;margin:0 0 3px;}
  .au-card-sub{font-size:13px;color:#6B7280;margin:0 0 18px;}

  /* two-col layout */
  .au-grid{display:grid;grid-template-columns:1.6fr 1fr;gap:20px;align-items:start;}
  .au-col{display:flex;flex-direction:column;gap:16px;}

  /* form grid */
  .au-fg{display:grid;grid-template-columns:1fr 1fr;gap:16px 20px;}
  .au-field{display:flex;flex-direction:column;gap:5px;}
  .au-label{font-size:13px;font-weight:600;color:#374151;}
  .au-req{color:#EF4444;margin-left:2px;}

  /* inputs */
  .au-input{height:40px;border:1px solid #E5E7EB;border-radius:9px;background:#fff;padding:0 12px;
    font-size:13.5px;font-family:inherit;color:#111827;width:100%;box-sizing:border-box;
    outline:none;transition:border-color .15s;}
  .au-input::placeholder{color:#9CA3AF;}
  .au-input:focus{border-color:#5B3DF5;box-shadow:0 0 0 3px rgba(91,61,245,.08);}

  /* select wrapper */
  .au-sw{position:relative;}
  .au-sw select{appearance:none;-webkit-appearance:none;cursor:pointer;padding-right:32px;}
  .au-sc{position:absolute;right:10px;top:50%;transform:translateY(-50%);pointer-events:none;color:#6B7280;}

  /* ── Phone trigger button ── */
  .au-phone-row{display:flex;gap:0;border:1px solid #E5E7EB;border-radius:9px;overflow:hidden;background:#fff;transition:border-color .15s;}
  .au-phone-row:focus-within{border-color:#5B3DF5;box-shadow:0 0 0 3px rgba(91,61,245,.08);}
  .au-dial-btn{
    display:flex;align-items:center;gap:5px;
    padding:0 10px;height:40px;background:#F9FAFB;
    border:none;border-right:1px solid #E5E7EB;
    cursor:pointer;font-size:13.5px;font-family:inherit;color:#111827;
    white-space:nowrap;flex-shrink:0;outline:none;
  }
  .au-dial-btn:hover{background:#F3F4F6;}
  .au-phone-num{flex:1;height:40px;border:none;outline:none;padding:0 12px;
    font-size:13.5px;font-family:inherit;color:#111827;background:transparent;}
  .au-phone-num::placeholder{color:#9CA3AF;}

  /* ── Country dropdown panel ── */
  .au-dd-wrap{position:relative;}
  .au-dd-panel{
    position:absolute;top:calc(100% + 4px);left:0;z-index:200;
    background:#fff;border:1px solid #E5E7EB;border-radius:10px;
    box-shadow:0 8px 24px rgba(0,0,0,.10);min-width:220px;overflow:hidden;
  }
  .au-dd-item{
    display:flex;align-items:center;gap:10px;padding:9px 14px;
    cursor:pointer;font-size:13.5px;color:#111827;transition:background .1s;
  }
  .au-dd-item:hover{background:#F5F3FF;}
  .au-dd-item.sel{background:#EDE9FE;font-weight:600;}
  .au-dd-flag{font-size:18px;line-height:1;}
  .au-dd-dial{font-weight:600;color:#5B3DF5;min-width:38px;}
  .au-dd-name{color:#6B7280;font-size:12.5px;}

  /* mfa */
  .au-mfa{display:flex;align-items:center;gap:8px;height:40px;}
  .au-cb{width:16px;height:16px;accent-color:#5B3DF5;cursor:pointer;}
  .au-mfa-txt{font-size:13.5px;color:#374151;font-weight:500;}

  /* password */
  .au-pw{position:relative;}
  .au-pw .au-input{padding-right:40px;}
  .au-eye{position:absolute;right:11px;top:50%;transform:translateY(-50%);
    background:none;border:none;padding:0;cursor:pointer;color:#6B7280;display:flex;align-items:center;}

  /* info box */
  .au-info{display:flex;align-items:flex-start;gap:10px;background:#EEF2FF;
    border-radius:9px;padding:11px 13px;margin-top:14px;}
  .au-info svg{color:#5B3DF5;flex-shrink:0;margin-top:1px;}
  .au-info-txt{font-size:12.5px;color:#4B5563;line-height:1.55;}
  .au-info.gray{background:#F3F4F6;}
  .au-info.gray svg{color:#6B7280;}

  /* role chips */
  .au-chips{display:flex;gap:8px;margin-bottom:14px;}
  .au-chip{flex:1;text-align:center;padding:7px 6px;border-radius:8px;
    font-size:12.5px;font-weight:600;cursor:pointer;
    border:1.5px solid #E5E7EB;color:#6B7280;background:#fff;transition:all .15s;}
  .au-chip.sel{background:#5B3DF5;color:#fff;border-color:#5B3DF5;}

  /* role preview */
  .au-role-box{border:1px solid #EDE9FE;border-radius:10px;padding:14px;
    background:#FAFAFF;display:flex;gap:12px;align-items:flex-start;}
  .au-role-ico{width:34px;height:34px;border-radius:9px;background:#EDE9FE;
    display:flex;align-items:center;justify-content:center;color:#5B3DF5;flex-shrink:0;}
  .au-role-name{font-size:13.5px;font-weight:700;color:#111827;margin:0 0 4px;}
  .au-role-desc{font-size:12.5px;color:#6B7280;margin:0 0 8px;line-height:1.5;}
  .au-role-lnk{font-size:12px;font-weight:600;color:#5B3DF5;text-decoration:none;
    display:inline-flex;align-items:center;gap:4px;}

  /* perm rows */
  .au-perm{display:flex;align-items:center;justify-content:space-between;
    padding:9px 0;border-bottom:1px solid #F3F4F6;}
  .au-perm:last-child{border-bottom:none;}
  .au-perm-l{display:flex;align-items:center;gap:9px;font-size:13px;color:#374151;font-weight:500;}
  .au-perm-l svg{color:#9CA3AF;}
  .au-perm-chk{color:#22C55E;}

  /* review */
  .au-rv-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px 28px;}
  .au-rv-k{font-size:11.5px;color:#9CA3AF;margin-bottom:3px;font-weight:500;
    text-transform:uppercase;letter-spacing:.4px;}
  .au-rv-v{font-size:13.5px;font-weight:600;color:#111827;}

  /* step-1 perm rows */
  .au-perm2{display:flex;align-items:center;justify-content:space-between;
    padding:10px 0;border-bottom:1px solid #F3F4F6;}
  .au-perm2:last-child{border-bottom:none;}

  /* footer */
  .au-footer{display:flex;justify-content:space-between;align-items:center;
    margin-top:20px;padding-top:16px;border-top:1px solid #F3F4F6;}
  .au-btn{height:44px;padding:0 22px;border-radius:10px;font-size:13.5px;font-weight:600;
    cursor:pointer;display:flex;align-items:center;gap:7px;border:none;transition:background .15s;}
  .au-btn-back{background:#fff;border:1px solid #E5E7EB;color:#374151;}
  .au-btn-back:hover{background:#F9FAFB;}
  .au-btn-next{background:#5B3DF5;color:#fff;}
  .au-btn-next:hover{background:#4C2FE0;}

  @media(max-width:860px){
    .au-grid{grid-template-columns:1fr;}
    .au-fg{grid-template-columns:1fr;}
  }
`;

/* ─── Field wrapper ──────────────────────────────────────── */
function F({ label, required, children }) {
  return (
    <div className="au-field">
      <label className="au-label">{label}{required && <span className="au-req">*</span>}</label>
      {children}
    </div>
  );
}

/* ─── Step bar — same pattern as RequestSignaturePage ────── */
const STEP_CIRCLE_CLASS = {
  done: "bg-green-500 text-white",
  current: "bg-blue-600 text-white",
  upcoming: "bg-gray-200 text-gray-600",
};

const STEP_LABEL_CLASS = {
  done: "text-green-600",
  current: "text-blue-600",
  upcoming: "text-gray-500",
};

function getStepState(index, currentStep) {
  if (index < currentStep) return "done";
  if (index === currentStep) return "current";
  return "upcoming";
}

function getNextButtonLabel(step, creating) {
  if (step < STEPS.length - 1) return `Next: ${STEPS[step + 1]}`;
  return creating ? "Creating..." : "Create User";
}

function StepBar({ step }) {
  return (
    <div className="flex items-center justify-between relative" style={{ height: "72px", marginBottom: "20px" }}>
      {STEPS.map((label, i) => {
        const state = getStepState(i, step);

        return (
          <div key={label} className="flex-1 flex flex-col items-center relative">
            {i !== STEPS.length - 1 && (
              <div className={`absolute top-5 left-1/2 w-full h-[2px] ${state === "done" ? "bg-green-500" : "bg-gray-300"}`} />
            )}
            <div
              className={`z-10 rounded-full flex items-center justify-center text-xs font-semibold ${STEP_CIRCLE_CLASS[state]}`}
              style={{ width: 40, height: 40 }}
            >
              {state === "done" ? <Check size={14} /> : i + 1}
            </div>
            <p className={`mt-1 text-[13px] font-medium text-center ${STEP_LABEL_CLASS[state]}`}>
              {label}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Custom phone dial-code picker ─────────────────────── */
function PhoneField({ dialCode, setDialCode, phone, setPhone }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const sel = COUNTRIES.find(c => c.code === dialCode) || COUNTRIES[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="au-dd-wrap" ref={ref}>
      <div className="au-phone-row">
        {/* trigger */}
        <button type="button" className="au-dial-btn" onClick={() => setOpen(o => !o)}>
          <span className="au-dd-flag">{sel.flag}</span>
          <span style={{ fontWeight: 600, color: "#5B3DF5", fontSize: 13 }}>{sel.dial}</span>
          <ChevronDown size={13} style={{ color: "#6B7280", marginLeft: 2 }} />
        </button>
        {/* number input */}
        <input
          className="au-phone-num"
          placeholder="Enter phone number"
          value={phone}
          onChange={e => setPhone(e.target.value)}
        />
      </div>

      {/* dropdown panel */}
      {open && (
        <div className="au-dd-panel">
          {COUNTRIES.map(c => (
            <div
              key={c.code}
              className={`au-dd-item ${c.code === dialCode ? "sel" : ""}`}
              onClick={() => { setDialCode(c.code); setOpen(false); }}
            >
              <span className="au-dd-flag">{c.flag}</span>
              <span className="au-dd-dial">{c.dial}</span>
              <span className="au-dd-name">{c.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────── */
export default function AddUserWizard() {
  const navigate = useNavigate();

  const [step, setStep]     = useState(0);
  const [role, setRole]     = useState("User");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [dialCode, setDialCode] = useState("in");
  const [phone, setPhone]   = useState("");
  const [form, setForm]     = useState({
    fullName: "", email: "", username: "",
    organization: "", department: "", jobTitle: "", location: "",
    status: "Active", mfa: true, password: "", confirm: "",
    permissions: {},
  });

  const set = k => e => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  // reset permission checkboxes to that role's defaults whenever role changes
  useEffect(() => {
    const defaults = {};
    ROLES[role].perms.forEach(p => { defaults[p.label] = true; });
    setForm(f => ({ ...f, permissions: defaults }));
  }, [role]);

  const [creating, setCreating] = useState(false);
const [error, setError] = useState("");
const [success, setSuccess] = useState("");

const createUser = async () => {

  setError("");
  setSuccess("");

  // =========================================
  // VALIDATION
  // =========================================

  if (!form.fullName.trim()) {
    setError("Full Name is required.");
    return;
  }

  if (!form.email.trim()) {
    setError("Email Address is required.");
    return;
  }

  if (!form.username.trim()) {
    setError("Username is required.");
    return;
  }

  if (!form.organization.trim()) {
    setError("Organization is required.");
    return;
  }

  if (!form.password) {
    setError("Password is required.");
    return;
  }

  if (form.password.length < 8) {
    setError(
      "Password must be at least 8 characters."
    );
    return;
  }

  if (form.password !== form.confirm) {
    setError(
      "Password and Confirm Password do not match."
    );
    return;
  }

  try {

    setCreating(true);

    const storedUser = JSON.parse(localStorage.getItem("user")) || {};

    const payload = {
      name: form.fullName,
      email: form.email,
      username: form.username,
      phone: phone
        ? `${selCountry.dial} ${phone}`
        : "",
      organization: form.organization,
      department: form.department,
      job_title: form.jobTitle,
      location: form.location,
      status: form.status,
      mfa: form.mfa,
      password: form.password,
      role: role,
      profile: "Standard",
      permissions: form.permissions,
      createdBy: storedUser.email
    };

    console.log(
      "CREATE USER PAYLOAD =",
      payload
    );

    const response = await axios.post(
      `${API_URL}/users`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      }
    );

    console.log(
      "CREATE USER RESPONSE =",
      response.data
    );

    setSuccess(
      "User created successfully."
    );

    // Give the success message a moment
    // before returning to Users Control.
    setTimeout(() => {
      navigate("/users-control");
    }, 1200);

  } catch (err) {

    console.error(
      "CREATE USER ERROR =",
      err
    );

    const message =
      err.response?.data?.error ||
      "Unable to create user.";

    setError(message);

  } finally {

    setCreating(false);

  }
};

  const RoleData = ROLES[role];
  const RoleIcon = RoleData.icon;
  const selCountry = COUNTRIES.find(c => c.code === dialCode) || COUNTRIES[0];

  return (
    <div className="au-wrap">
      <style>{CSS}</style>

      {/* ── Step bar ── */}
      <StepBar step={step} />

      {/* ══════════════ STEP 0 ══════════════ */}
      {step === 0 && (
        <div className="au-grid">

          {/* LEFT col */}
          <div className="au-col">

            {/* User Info card */}
            <div className="au-card">
              <h3 className="au-card-title">User Information</h3>
              <p className="au-card-sub">Enter the basic details of the new user.</p>
              <div className="au-fg">

                <F label="Full Name" required>
                  <input className="au-input" placeholder="Enter full name" value={form.fullName} onChange={set("fullName")} />
                </F>

                <F label="Email Address" required>
                  <input className="au-input" placeholder="Enter email address" value={form.email} onChange={set("email")} />
                </F>

                <F label="Username" required>
                  <input className="au-input" placeholder="Enter username" value={form.username} onChange={set("username")} />
                </F>

                <F label="Phone Number">
                  <PhoneField dialCode={dialCode} setDialCode={setDialCode} phone={phone} setPhone={setPhone} />
                </F>

                <F label="Organization" required>
                  <div className="au-sw">
                    <select className="au-input" value={form.organization} onChange={set("organization")}>
                      <option value="">Select organization</option>
                      <option>Acme Corp</option>
                      <option>Globex Inc</option>
                    </select>
                    <ChevronDown size={15} className="au-sc" />
                  </div>
                </F>

                <F label="Department">
                  <div className="au-sw">
                    <select className="au-input" value={form.department} onChange={set("department")}>
                      <option value="">Select department</option>
                      <option>Engineering</option>
                      <option>Sales</option>
                      <option>Legal</option>
                    </select>
                    <ChevronDown size={15} className="au-sc" />
                  </div>
                </F>

                <F label="Job Title">
                  <input className="au-input" placeholder="Enter job title" value={form.jobTitle} onChange={set("jobTitle")} />
                </F>

                <F label="Location">
                  <input className="au-input" placeholder="Enter location" value={form.location} onChange={set("location")} />
                </F>

                <F label="Status">
                  <div className="au-sw">
                    <select className="au-input" value={form.status} onChange={set("status")}>
                      <option>Active</option>
                      <option>Inactive</option>
                      <option>Pending</option>
                    </select>
                    <ChevronDown size={15} className="au-sc" />
                  </div>
                </F>

                <F label="MFA">
                  <div className="au-mfa">
                    <input type="checkbox" className="au-cb" id="mfa" checked={form.mfa} onChange={set("mfa")} />
                    <label htmlFor="mfa" className="au-mfa-txt">Enable Multi-Factor Authentication</label>
                  </div>
                </F>

              </div>
            </div>

            {/* Password card */}
            <div className="au-card">
              <h3 className="au-card-title">Set Password</h3>
              <p className="au-card-sub">Set an initial password for the user.</p>
              <div className="au-fg">
                <F label="Password" required>
                  <div className="au-pw">
                    <input className="au-input" type={showPw ? "text" : "password"} placeholder="••••••••••••" value={form.password} onChange={set("password")} />
                    <button type="button" className="au-eye" onClick={() => setShowPw(v => !v)}>
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </F>
                <F label="Confirm Password" required>
                  <div className="au-pw">
                    <input className="au-input" type={showPw2 ? "text" : "password"} placeholder="••••••••••••" value={form.confirm} onChange={set("confirm")} />
                    <button type="button" className="au-eye" onClick={() => setShowPw2(v => !v)}>
                      {showPw2 ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </F>
              </div>
              <div className="au-info">
                <Info size={15} />
                <span className="au-info-txt">The user will be required to change this password on their first login.</span>
              </div>
            </div>

          </div>{/* /LEFT */}

          {/* RIGHT col */}
          <div className="au-col">

            {/* Assign Role */}
            <div className="au-card">
              <h3 className="au-card-title">Assign Role</h3>
              <p className="au-card-sub">Select a role for the user.</p>
              <div className="au-chips">
                {Object.keys(ROLES).map(r => (
                  <div key={r} className={`au-chip ${role === r ? "sel" : ""}`} onClick={() => setRole(r)}>{r}</div>
                ))}
              </div>
              <div className="au-role-box">
                <div className="au-role-ico"><RoleIcon size={17} /></div>
                <div>
                  <p className="au-role-name">{role}</p>
                  <p className="au-role-desc">{RoleData.desc}</p>
                  <a href="#" className="au-role-lnk">View All Roles <ExternalLink size={12} /></a>
                </div>
              </div>
            </div>

            {/* Permissions preview */}
            <div className="au-card">
              <h3 className="au-card-title">Role Permissions (Preview)</h3>
              <p className="au-card-sub">Permissions included in the selected role.</p>
              {RoleData.perms.map(({ label, icon: Icon }) => (
                <div className="au-perm" key={label}>
                  <div className="au-perm-l"><Icon size={14} />{label}</div>
                  <CheckCircle2 size={16} className="au-perm-chk" />
                </div>
              ))}
            </div>

            <div className="au-info gray">
              <Info size={15} />
              <span className="au-info-txt">You can customize permissions for this user after creating the account.</span>
            </div>

          </div>{/* /RIGHT */}
        </div>
      )}

      {/* ══════════════ STEP 1 ══════════════ */}
      {step === 1 && (
        <div className="au-card">
          <h3 className="au-card-title">Roles & Permissions</h3>
          <p className="au-card-sub">Fine-tune exactly what {form.fullName || "this user"} can do.</p>
          <div className="au-chips" style={{ maxWidth: 380, marginBottom: 18 }}>
            {Object.keys(ROLES).map(r => (
              <div key={r} className={`au-chip ${role === r ? "sel" : ""}`} onClick={() => setRole(r)}>{r}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 32px" }}>
            {RoleData.perms.map(({ label, icon: Icon }) => (
              <div className="au-perm2" key={label}>
                <div className="au-perm-l"><Icon size={14} />{label}</div>
                <input
                  type="checkbox"
                  className="au-cb"
                  checked={!!form.permissions[label]}
                  onChange={e =>
                    setForm(f => ({
                      ...f,
                      permissions: { ...f.permissions, [label]: e.target.checked },
                    }))
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════ STEP 2 ══════════════ */}
      {step === 2 && (
        <div className="au-card">
          <h3 className="au-card-title">Review & Confirm</h3>
          <p className="au-card-sub">Double-check the details before creating this account.</p>
          <div className="au-rv-grid">
            {[
              ["Full Name",    form.fullName],
              ["Email",        form.email],
              ["Username",     form.username],
              ["Phone",        phone ? `${selCountry.flag} ${selCountry.dial} ${phone}` : "—"],
              ["Organization", form.organization],
              ["Department",   form.department],
              ["Job Title",    form.jobTitle],
              ["Location",     form.location],
              ["Status",       form.status],
              ["MFA",          form.mfa ? "Enabled" : "Disabled"],
              ["Role",         role],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="au-rv-k">{k}</div>
                <div className="au-rv-v">{v || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
  <div
    style={{
      marginTop: 16,
      padding: "11px 14px",
      borderRadius: 9,
      background: "#FEF2F2",
      border: "1px solid #FECACA",
      color: "#B91C1C",
      fontSize: 13,
      fontWeight: 500
    }}
  >
    {error}
  </div>
)}

{success && (
  <div
    style={{
      marginTop: 16,
      padding: "11px 14px",
      borderRadius: 9,
      background: "#F0FDF4",
      border: "1px solid #BBF7D0",
      color: "#15803D",
      fontSize: 13,
      fontWeight: 500
    }}
  >
    {success}
  </div>
)}

      {/* ── Footer ── */}
      <div className="au-footer">
        <button
          className="au-btn au-btn-back"
          onClick={() => step === 0 ? navigate("/users-control") : setStep(s => s - 1)}
        >
          <ArrowLeft size={15} /> {step === 0 ? "Cancel" : "Back"}
        </button>
        <button
  className="au-btn au-btn-next"
  disabled={creating}
  onClick={() => {
    if (step < 2) {
      setStep(s => s + 1);
    } else {
      createUser();
    }
  }}
>
  {getNextButtonLabel(step, creating)}

  {step < 2 && (
    <ArrowRight size={15} />
  )}
</button>
      </div>
    </div>
  );
}